import { useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import ImageSkeleton from '@/components/shared/ImageSkeleton';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useTranslation } from 'next-i18next';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';

const BlogCard = ({ blog, t, lang }) => {
    const [imageLoaded, setImageLoaded] = useState(false)
    const [imageFailed, setImageFailed] = useState(false)
    return (
        <div className="bg-[#F5F7F9] rounded-[40px] overflow-hidden">
            <div className="flex flex-col h-full px-[16px] py-[24px] gap-[16px]">
                <div className="relative w-full h-[200px] lg:h-[220px] overflow-hidden">
                    {!imageLoaded && (
                        <ImageSkeleton className="absolute inset-0 w-full h-full rounded-[20px]" />
                    )}
                    {!!blog.media?.[0] && !imageFailed && (
                        <Image
                            src={blog.media[0]}
                            alt={blog.title}
                            fill
                            priority
                            onLoad={() => setImageLoaded(true)}
                            onError={() => {
                                setImageFailed(true)
                                setImageLoaded(false)
                            }}
                            className={`object-cover object-[center_-10px] rounded-[20px] transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                        />
                    )}
                </div>
            <div className="flex flex-col flex-grow">
                <p className="text-[#222] text-[16px] md:text-[20px] font-[600] leading-relaxed mb-[20px] flex-grow line-clamp-3">
                    {blog.title}
                </p>
                <Link
                    href={lang ? `/blogs/${blog.id}?lang=${lang}` : `/blogs/${blog.id}`}
                    className="relative self-end overflow-hidden w-[160px] h-[48px] flex items-center justify-center rounded-full text-white hover:text-[#091F59] transition-colors duration-300 group"
                >
                    <span className="absolute inset-0 bg-gradient-to-b from-[#091F59] to-[#1A3884] border border-[#091F59] transition-opacity duration-300 group-hover:opacity-0" />
                    <span className="absolute inset-0 bg-white opacity-0 border rounded-full border-[#091F59] transition-opacity duration-300 group-hover:opacity-100" />
                    <span className="relative z-10 text-[16px] font-[400]">
                        {t('blogs.read-more')}
                    </span>
                </Link>
            </div>
        </div>
    </div>
    );
};

const SimilarBlogs = ({ blogs }) => {
    const { t } = useTranslation();
    const router = useRouter();
    const lang = router.query.lang;
    const sliderRef = useRef(null);
    const useSlider = blogs && blogs.length > 3;

    if (!blogs || blogs.length === 0) return null;

    const settings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 3,
        slidesToScroll: 1,
        arrows: false,
        responsive: [
            { breakpoint: 1024, settings: { slidesToShow: 2, slidesToScroll: 1 } },
            { breakpoint: 640, settings: { slidesToShow: 1, slidesToScroll: 1 } },
        ],
    };

    return (
        <div className="max-w-[90%] mx-auto py-[100px]">
            <div className={`flex mb-[32px] ${useSlider ? 'justify-between' : ''}`}>
                <h2 className="text-[24px] md:text-[36px] lg:text-[48px] font-[500] text-transparent bg-clip-text bg-gradient-to-b from-[#091F59] to-[#1A3884]">
                    {t('blogs.similar-blogs')}
                </h2>
                {useSlider && (
                    <div className="flex items-center gap-[12px]">
                        <button
                            onClick={() => sliderRef.current?.slickPrev()}
                            className="w-[40px] h-[40px] flex items-center justify-center rounded-full border-[1.5px] border-[#E2E8F0] text-[#091F59] hover:bg-[#F5F7F9] transition-colors"
                        >
                            <IoIosArrowBack size={18} />
                        </button>
                        <button
                            onClick={() => sliderRef.current?.slickNext()}
                            className="w-[40px] h-[40px] flex items-center justify-center rounded-full border-[1.5px] border-[#E2E8F0] text-[#091F59] hover:bg-[#F5F7F9] transition-colors"
                        >
                            <IoIosArrowForward size={18} />
                        </button>
                    </div>
                )}
            </div>

            {useSlider ? (
                <Slider ref={sliderRef} {...settings}>
                    {blogs.map((blog) => (
                        <div key={blog.id} className="px-[12px]">
                            <BlogCard blog={blog} t={t} lang={lang} />
                        </div>
                    ))}
                </Slider>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[24px]">
                    {blogs.map((blog) => (
                        <div key={blog.id}>
                            <BlogCard blog={blog} t={t} lang={lang} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SimilarBlogs;
